import * as React from "react";
import Cookies from "universal-cookie";
import {
  API_URL,
  BOOK_ACCESS,
  SIGN_IN_SESSION_COOKIE,
  BOOK_SESSION_USER_IP_COOKIE,
} from "../utils/constants";
import { logError } from "../utils/errorTracker";

export const UserContext = React.createContext<any | null>(null);

const UserProvider = ({ children }: any) => {
  const [todos, setTodos] = React.useState<any[]>([
    {
      id: 1,
      title: "post 1",
      description: "this is a description",
      status: false,
    },
    {
      id: 2,
      title: "post 2",
      description: "this is a description",
      status: true,
    },
  ]);

  const [userData, setUserData] = React.useState<any[]>([]);
  const [userDataLoaded, setUserDataLoaded] = React.useState(false);

  React.useEffect(() => {
    const cookies = new Cookies();
    if (cookies.get(SIGN_IN_SESSION_COOKIE)) {
      getUser(cookies);
    } else {
      setUserDataLoaded(true);
    }
  }, []);

  const getUser = async (cookies: Cookies) => {
    const userEmail = cookies.get(SIGN_IN_SESSION_COOKIE);

    try {
      const response = await fetch(`${API_URL}/api/get-user`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          userEmail: userEmail,
          bookToAccess: BOOK_ACCESS,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseJson = await response.json();

      if (responseJson.body?.user?.[0]) {
        const user = responseJson.body.user[0];
        if (user.user_ip === cookies.get(BOOK_SESSION_USER_IP_COOKIE)) {
          setUserData(user);
        } else {
          cookies.remove(SIGN_IN_SESSION_COOKIE);
          cookies.remove(BOOK_SESSION_USER_IP_COOKIE);
        }
      }
    } catch (error) {
      console.error("❌ UserContext: Error fetching user data:", error);
      console.error("❌ UserContext: Error details:", {
        message: error instanceof Error ? error.message : "Unknown error",
        stack: error instanceof Error ? error.stack : undefined,
        apiUrl: `${API_URL}/api/get-user`,
      });

      // Log error to tracker
      await logError("UserContext", "API_ERROR", "Error fetching user data", {
        apiUrl: `${API_URL}/api/get-user`,
        email: userEmail,
        stack: error instanceof Error ? error.stack : undefined,
        message: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setUserDataLoaded(true);
    }
  };

  const userLogout = async () => {
    try {
      const cookies = new Cookies();
      cookies.remove(SIGN_IN_SESSION_COOKIE);
      cookies.remove(BOOK_SESSION_USER_IP_COOKIE);
      setUserData([]);
      setUserDataLoaded(true);
      window.location.href = "/";
    } catch (error) {
      console.error("❌ UserContext: Error during logout:", error);

      // Log error to tracker
      await logError("UserContext", "UNKNOWN_ERROR", "Error during logout", {
        stack: error instanceof Error ? error.stack : undefined,
        message: error instanceof Error ? error.message : "Unknown error",
      });
    }
  };

  const saveTodo = async (todo: any) => {
    try {
      const newTodo: any = {
        id: Math.random(),
        title: todo.title,
        description: todo.description,
        status: false,
      };
      setTodos([...todos, newTodo]);
    } catch (error) {
      console.error("❌ UserContext: Error saving todo:", error);

      // Log error to tracker
      await logError("UserContext", "UNKNOWN_ERROR", "Error saving todo", {
        stack: error instanceof Error ? error.stack : undefined,
        message: error instanceof Error ? error.message : "Unknown error",
        todoData: todo,
      });
    }
  };

  const updateTodo = async (id: number) => {
    try {
      const updatedTodos = todos.map((todo: any) => {
        if (todo.id === id) {
          return { ...todo, status: true };
        }
        return todo;
      });
      setTodos(updatedTodos);
    } catch (error) {
      console.error("❌ UserContext: Error updating todo:", error);

      // Log error to tracker
      await logError("UserContext", "UNKNOWN_ERROR", "Error updating todo", {
        stack: error instanceof Error ? error.stack : undefined,
        message: error instanceof Error ? error.message : "Unknown error",
        todoId: id,
      });
    }
  };

  return (
    <UserContext.Provider
      value={{
        todos,
        userData,
        userDataLoaded,
        saveTodo,
        updateTodo,
        userLogout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserProvider;
